import classNames from 'classnames'
import { I18nT, useI18n } from 'i18n-utils'
import { OrderDirection } from 'network'
import { useExchangeInfoStore, useProjectTheme, UserDoubleContractUnitType } from 'store'
import './StopProfitStopLoss.scss'
import { StorageIndexDBEnum } from 'store/src/storage/StorageDB'
import { DefineComponentProps } from 'utils'
import { CustomButton } from '../components/Button'
import { DialogHeader } from '../components/DialogHeader'
import { InputView } from '../components/InputView'
import { ListCell } from '../components/ListCell'
import { Tag } from '../components/Tag'
import { HocElDialog } from '../hoc-element-components/HocElDialog'
import { HocElSlider } from '../hoc-element-components/HocElSlider'
import { ToggleIcon } from '../icons/ToggleIcon'
import { PositionInfoLayout } from '../layouts/PositionInfo.layout'
import { useStopProfitStopLoss } from '../stores/use-stop-profit-stop-loss'

export type StopProfitStopLossProps = {
  position: ApiResponse.DoubleContractPosition
  onClose?: () => void
}


export const StopProfitStopLoss = defineComponent<StopProfitStopLossProps>({
  props: DefineComponentProps<StopProfitStopLossProps>(['position', 'onClose']),
  setup(props) {
    const stopProfitStopLoss = useStopProfitStopLoss(props.position)
    const { t } = useI18n()
    const exchangeInfoStore = useExchangeInfoStore()
    const themeStore = useProjectTheme()
    const theme = computed(() => themeStore.theme)
    const userDoubleContractUnitType = window.localStorage.getItem(StorageIndexDBEnum.UserDoubleContractUnitType) || UserDoubleContractUnitType.USDT

    onBeforeMount(() => {
      stopProfitStopLoss.open()
    })

    const onClose = () => {
      if (!stopProfitStopLoss.stopProfitStopLossVisible.value) return
      stopProfitStopLoss.close()
      props.onClose?.()
    }

    const closeName = computed(() => {
      if (props.position.direction === OrderDirection.Buy) {
        return t('平多')
      } else {
        return t('平空')
      }
    })

    const coinToPrecision = computed(() => {
      const data = exchangeInfoStore.getContractSymbolInfoBySymbolId(props.position.symbolId)
      return data!.pricePrecision
    })

    const coinFromPrecision = computed(() => {
      const data = exchangeInfoStore.getContractSymbolInfoBySymbolId(props.position.symbolId)
      return data!.basePrecision
    })

    const amountPrecision = computed(() => {
      if (userDoubleContractUnitType === UserDoubleContractUnitType.USDT) {
        return coinToPrecision.value
      }
      return coinFromPrecision.value
    })

    return () => (
      <HocElDialog width={550} v-model={stopProfitStopLoss.stopProfitStopLossVisible.value} showClose={false} onClose={onClose}>
        {{
          header: () => <DialogHeader onClose={onClose}>{t('止盈止损')}</DialogHeader>,
          default: () => (
            <div class="stop-profit-stop-loss-layout">
              <PositionInfoLayout>
                <div class="coin-info">
                  <div class="icon-wrapper">
                    <img src={stopProfitStopLoss.getSymbolIcon()} alt="coin" />
                  </div>
                  <div>
                    <div class="coin-name">
                      {stopProfitStopLoss.symbolInfo.value?.symbolName?.toUpperCase()}
                      {t('永续')}
                    </div>
                    <div class="position-info">
                      <Tag color={props.position.direction === OrderDirection.Sell ? theme.value.colors.up : theme.value.colors.down}>{closeName.value}</Tag>
                      <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                        {props.position.marginType === 1 ? t('逐仓') : t('全仓')}
                      </Tag>
                      <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                        {props.position.leverage}X
                      </Tag>
                    </div>
                  </div>
                </div>
                <div class="position-info-list">
                  <ListCell title={t('开仓价格')} description={props.position.holdPrice.formatNumberWithCommas()} />
                  <ListCell title={t('标记价格')} description={stopProfitStopLoss.markPrice.value?.formatNumberWithCommas()} />
                  <ListCell title={t('最新价格')} description={stopProfitStopLoss.lastPrice.value?.formatNumberWithCommas()} />
                  <ListCell class="liquidation-price" title={t('强平价格')} description={props.position.liquidationPrice.formatNumberWithCommas()} />
                </div>
                <div class="position-form">
                  <div class="position-form-item type">
                    <span class="place-order-type">{t('仓位类型')}</span>
                    <span class="place-order-type-value" onClick={stopProfitStopLoss.switchAllPosition}>
                      <ToggleIcon />
                      {t(stopProfitStopLoss.allPosition.value ? '全部仓位' : '部分仓位')}
                    </span>
                  </div>
                  {!stopProfitStopLoss.allPosition.value && (
                    <>
                      <div class="position-form-item">
                        <InputView
                          placeholder={t('数量')}
                          v-model={stopProfitStopLoss.amount.value}
                          radius={6}
                          suffixIcon={() => 'USDT'}
                          onInput={stopProfitStopLoss.onAmountInput}
                          isFloatNumber
                          maxDecimalLength={amountPrecision.value}
                        />
                        <div class="total-amount">
                          <span class="total-amount-label">{t('总持仓量')}</span>
                          <strong>{stopProfitStopLoss.totalAmount.value.formatCommonMoney()}</strong>
                          <span class="total-amount-label">USDT</span>
                        </div>
                        <HocElSlider
                          marks={stopProfitStopLoss.sliderMarks}
                          v-model={stopProfitStopLoss.sliderValue.value}
                          onInput={(value) => stopProfitStopLoss.onSliderInput(value as number)}
                        />
                      </div>
                    </>
                  )}
                  <div class="market-price-profit">
                    <div class="market-price">
                      <span class="market-price-label">{t('市价止盈')}</span>
                      <span class="market-price-value" onClick={stopProfitStopLoss.switchMarketPrice}>
                        <ToggleIcon />
                        <span>{stopProfitStopLoss.isMarketPrice.value ? t('标记价格') : t('最新价格')}</span>
                      </span>
                    </div>
                    <div class="flex align-center input-box">
                      <InputView
                        isFloatNumber
                        suffixIcon={() => t('触发价格') + '(USDT)'}
                        v-model={stopProfitStopLoss.profitPrice.value}
                        radius={6}
                        onInput={(value) => stopProfitStopLoss.onProfitPriceInput(value)}
                        maxDecimalLength={coinToPrecision.value}
                      />
                      <InputView
                        isFloatNumber
                        maxDecimalLength={2}
                        suffixIcon={() => t('收益率') + '(%)'}
                        v-model={stopProfitStopLoss.profitRate.value}
                        radius={6}
                        allowNegative
                        onInput={(value) => stopProfitStopLoss.onProfitRateInput(value)}
                      />
                    </div>
                    <HocElSlider
                      marks={stopProfitStopLoss.profitRateSliderMarks}
                      modelValue={stopProfitStopLoss.sliderProfitRateValue.value}
                      max={200}
                      step={0.01}
                      onInput={(value) => stopProfitStopLoss.onProfitRateSliderInput(value as number)}
                      formatTooltip={(value) => `${value}%`}
                    />
                    <div class="profit-loss-info">
                      <I18nT keypath="当{condition}时，将市价平仓，预计收益为{profit}">
                        {{
                          condition: () => (
                            <span class={'up'}>
                              {stopProfitStopLoss.isMarketPrice.value ? t('标记价格') : t('最新价格')}
                              {props.position.direction === OrderDirection.Buy ? '≥' : '≤'}
                              {stopProfitStopLoss.profitPrice.value.formatNumberWithCommas()}
                              USDT
                            </span>
                          ),
                          profit: () => (
                            <span
                              class={classNames({
                                up: stopProfitStopLoss.profit.value.isGreaterThan(0),
                                down: stopProfitStopLoss.profit.value.isLessThan(0),
                              })}
                            >
                              {stopProfitStopLoss.profit.value.formatNumberWithCommas()}USDT({stopProfitStopLoss.profitRate.value}%)
                            </span>
                          ),
                        }}
                      </I18nT>
                    </div>
                  </div>

                  <div class="market-price-profit loss">
                    <div class="market-price">
                      <span class="market-price-label">{t('市价止损')}</span>
                      <span class="market-price-value" onClick={stopProfitStopLoss.switchLossIsMarketPrice}>
                        <ToggleIcon />
                        <span>{stopProfitStopLoss.lossIsMarketPrice.value ? t('标记价格') : t('最新价格')}</span>
                      </span>
                    </div>
                    <div class="flex align-center input-box">
                      <InputView
                        isFloatNumber
                        suffixIcon={() => t('触发价格') + '(USDT)'}
                        v-model={stopProfitStopLoss.lossPrice.value}
                        radius={6}
                        onInput={(value) => stopProfitStopLoss.onLossPriceInput(value)}
                        maxDecimalLength={coinToPrecision.value}
                      />
                      <InputView
                        isFloatNumber
                        maxDecimalLength={2}
                        suffixIcon={() => t('收益率') + '(%)'}
                        v-model={stopProfitStopLoss.lossRate.value}
                        radius={6}
                        allowNegative
                        onInput={(value) => stopProfitStopLoss.onLossRateInput(value)}
                      />
                    </div>
                    <HocElSlider
                      marks={stopProfitStopLoss.lossRateSliderMarks}
                      modelValue={stopProfitStopLoss.sliderLossRateValue.value}
                      max={200}
                      min={0}
                      step={0.01}
                      formatTooltip={(value) => (value > 0 ? `-${value}%` : `${value}%`)}
                      onInput={(value) => stopProfitStopLoss.onLossRateSliderInput(value as number)}
                    />
                    <div class="profit-loss-info">
                      <I18nT keypath="当{condition}时，将市价平仓，预计收益为{profit}">
                        {{
                          condition: () => (
                            <span class={'down'}>
                              {stopProfitStopLoss.lossIsMarketPrice.value ? t('标记价格') : t('最新价格')}
                              {props.position.direction === OrderDirection.Buy ? '≥' : '≤'}
                              {stopProfitStopLoss.lossPrice.value.formatCommonMoney()}
                              USDT
                            </span>
                          ),
                          profit: () => (
                            <span
                              class={classNames({
                                up: stopProfitStopLoss.loss.value.isGreaterThan(0),
                                down: stopProfitStopLoss.loss.value.isLessThan(0),
                              })}
                            >
                              {stopProfitStopLoss.loss.value.formatNumberWithCommas()}USDT({stopProfitStopLoss.lossRate.value}%)
                            </span>
                          ),
                        }}
                      </I18nT>
                    </div>
                  </div>

                  <CustomButton class={'submit-btn'} type="primary" loading={stopProfitStopLoss.submitLoading.value} onClick={stopProfitStopLoss.onConfirmStopProfitStopLoss}>
                    {t('确认')}
                  </CustomButton>
                </div>
              </PositionInfoLayout>
            </div>
          ),
        }}
      </HocElDialog>
    )
  },
})
